import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [flutterApp, setFlutterApp] = useState(null);
  const [isFlutterReady, setIsFlutterReady] = useState(false);
  const [isSettingLanguageFromFlutter, setIsSettingLanguageFromFlutter] = useState(false);
  const hasInitializedRef = useRef(false);

  // Initialize language from localStorage, device language, or fallback to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      return savedLanguage;
    }

    // Detect browser language
    const browserLanguage = navigator.language.split('-')[0];
    const supportedLanguages = ['en', 'tr'];

    if (supportedLanguages.includes(browserLanguage)) {
      return browserLanguage;
    }

    return 'en'; // Fallback to English
  });

  // Listen for Flutter initialization
  useEffect(() => {
    const handleFlutterInitialized = (event) => {
      // Prevent duplicate initialization
      if (hasInitializedRef.current) {
        return;
      }

      // Flutter initialized successfully
      setFlutterApp(event.detail);
      setIsFlutterReady(true);
      hasInitializedRef.current = true;

      // Set up language change listener
      if (event.detail && typeof event.detail.onLanguageChanged === 'function') {
        event.detail.onLanguageChanged((newLanguage) => {
          setIsSettingLanguageFromFlutter(true);
          i18n.changeLanguage(newLanguage);
          localStorage.setItem('language', newLanguage);
          setCurrentLanguage(newLanguage);
          // Reset flag after a short delay to allow the change to propagate
          setTimeout(() => setIsSettingLanguageFromFlutter(false), 100);
        });
      }

      // Get initial language from Flutter but only if React doesn't have a saved language
      if (event.detail && typeof event.detail.getLanguage === 'function') {
        const initialFlutterLanguage = event.detail.getLanguage();
        const savedReactLanguage = localStorage.getItem('language');

        if (!savedReactLanguage) {
          // Use Flutter's language
          i18n.changeLanguage(initialFlutterLanguage);
          localStorage.setItem('language', initialFlutterLanguage);
          setCurrentLanguage(initialFlutterLanguage);
        } else {
          // Sync React's saved language to Flutter instead
          event.detail.setLanguage(savedReactLanguage);
        }
      }
    };

    // Listen only on document to avoid duplicate events
    document.addEventListener('flutter-initialized', handleFlutterInitialized);

    return () => {
      document.removeEventListener('flutter-initialized', handleFlutterInitialized);
    };
  }, [i18n]);

  // Update language (this will also update Flutter's language)
  const changeLanguage = useCallback((newLanguage) => {
    // Don't update if this change is coming from Flutter to prevent loop
    if (isSettingLanguageFromFlutter) {
      return;
    }

    i18n.changeLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    setCurrentLanguage(newLanguage);

    // Update Flutter language if app is ready
    if (isFlutterReady && flutterApp && typeof flutterApp.setLanguage === 'function') {
      flutterApp.setLanguage(newLanguage);
    }
  }, [i18n, isFlutterReady, flutterApp, isSettingLanguageFromFlutter]);

  const value = {
    currentLanguage,
    changeLanguage,
    isFlutterReady,
    flutterApp,
    i18n
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
